import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React, { useEffect } from "react";
import styled from "styled-components";
var ANIMATION_INTERVAL_MS = 2000;
var TRANSITION_DURATION_MS = 500;
export var OrderImportIllustration = function(param) {
    var visible = param.visible;
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _React_useState = _sliced_to_array(React.useState(true), 2), automicImportChecked = _React_useState[0], setAutomaticImportChecked = _React_useState[1];
    useEffect(function() {
        if (!visible) {
            return;
        }
        var intervalId = setInterval(function() {
            setAutomaticImportChecked(function(prevState) {
                return !prevState;
            });
        }, ANIMATION_INTERVAL_MS);
        return function() {
            return clearInterval(intervalId);
        };
    }, [
        visible
    ]);
    var automaticImportHeading = strictLocalize({
        id: "1v56lq",
        defaultMessage: [
            {
                type: 0,
                value: "Automatically import orders"
            }
        ]
    });
    var manualImportHeading = strictLocalize({
        id: "DxSX3j",
        defaultMessage: [
            {
                type: 0,
                value: "Review products first before importing them"
            }
        ]
    });
    return /*#__PURE__*/ _jsxs(Container, {
        gap: isTabletAndBelow ? "3x" : "4x",
        children: [
            /*#__PURE__*/ _jsx(SettingCard, {
                heading: automaticImportHeading,
                checked: automicImportChecked
            }),
            /*#__PURE__*/ _jsx(SettingCard, {
                heading: manualImportHeading,
                checked: !automicImportChecked
            })
        ]
    });
};
var SettingCard = function(param) {
    var heading = param.heading, checked = param.checked;
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    if (isTabletAndBelow) {
        return /*#__PURE__*/ _jsxs(SettingContainer, {
            gap: "1x",
            children: [
                /*#__PURE__*/ _jsx(RadioButton, {
                    checked: checked
                }),
                /*#__PURE__*/ _jsxs(TextContainer, {
                    gap: "1x",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraphSansMedium",
                            color: Core.text.subdued,
                            children: heading
                        }),
                        /*#__PURE__*/ _jsx(PlaceholderText, {
                            $width: "100%"
                        }),
                        /*#__PURE__*/ _jsx(PlaceholderText, {
                            $width: "85%"
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(SettingContainer, {
        gap: "3x",
        children: [
            /*#__PURE__*/ _jsx(RadioButton, {
                checked: checked
            }),
            /*#__PURE__*/ _jsxs(TextContainer, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "sectionHeaderSansMedium",
                        color: Core.text.subdued,
                        children: heading
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "3x"
                    }),
                    /*#__PURE__*/ _jsx(PlaceholderText, {
                        $width: "85%"
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsx(PlaceholderText, {
                        $width: "70%"
                    })
                ]
            })
        ]
    });
};
var RadioButton = function(param) {
    var checked = param.checked;
    return /*#__PURE__*/ _jsx(RadioButtonContainer, {
        children: /*#__PURE__*/ _jsx(RadioButtonDot, {
            $checked: checked
        })
    });
};
var Container = styled(Column).withConfig({
    componentId: "sc-26b8b47e-0"
})([
    "height:584px;width:100%;max-width:584px;background-color:",
    ";padding:",
    ";user-select:none;",
    "{height:343px;padding:",
    ";}"
], Decorative.icon.red, getSpacing("10x", "4x"), responsiveMediaQueries.tabletAndBelow, getSpacing("6x", "3x"));
var SettingContainer = styled(Row).withConfig({
    componentId: "sc-26b8b47e-1"
})([
    "height:192px;width:100%;padding:",
    ";border-radius:4px;background-color:",
    ";box-shadow:0px 4px 10px 0px rgba(0,0,0,0.1);",
    "{height:112px;padding:",
    ";}"
], getSpacing("4x"), Core.surface.inverse, responsiveMediaQueries.tabletAndBelow, getSpacing("2x"));
var TextContainer = styled(Column).withConfig({
    componentId: "sc-26b8b47e-2"
})([
    "width:100%;"
]);
var PlaceholderText = styled.div.withConfig({
    componentId: "sc-26b8b47e-3"
})([
    "width:",
    ";max-height:12px;background-color:",
    ";border-radius:1px;flex-grow:1;"
], function(props) {
    var _props_$width;
    return (_props_$width = props.$width) !== null && _props_$width !== void 0 ? _props_$width : "100%";
}, Core.border.muted);
var RadioButtonContainer = styled.div.withConfig({
    componentId: "sc-26b8b47e-4"
})([
    "width:",
    ";height:",
    ";border-radius:50%;border:2px solid ",
    ";position:relative;display:flex;align-items:center;justify-content:center;flex-shrink:0;",
    "{width:",
    ";height:",
    ";}"
], getSpacing("4x"), getSpacing("4x"), Core.border.subdued, responsiveMediaQueries.tabletAndBelow, getSpacing("2x"), getSpacing("2x"));
var RadioButtonDot = styled.div.withConfig({
    componentId: "sc-26b8b47e-5"
})([
    "width:",
    ";height:",
    ";border-radius:50%;background-color:",
    ";opacity:",
    ";transition:opacity ",
    "ms ease;",
    "{width:",
    ";height:",
    ";}"
], getSpacing("2x"), getSpacing("2x"), Core.icon.subdued, function(props) {
    return props.$checked ? 1 : 0;
}, TRANSITION_DURATION_MS, responsiveMediaQueries.tabletAndBelow, getSpacing("1x"), getSpacing("1x"));
